import { forwardRef } from 'react';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { MAX_CHAT_MESSAGE_LENGTH } from '../../constants/chatRequests.constants';
import { resetMobileViewportAfterKeyboard } from '@/common/hooks/useMobileViewportHeight';
import TrashIcon from '../../icons/Trash.svg';
import {
	Composer,
	ComposerActionButton,
	ComposerInput,
	SendActionButton,
} from '../../chat.styles';
import {
	VOICE_CALL_WAVE_CONFIG,
	WAVE_BARS_CONFIG,
} from './constants/chatInputBar.constants';
import {
	RecordingWrapper,
	TrashButton,
	VoiceCallComposer,
	VoiceCallStatus,
	VoiceCallWave,
	VoiceCallWaveBar,
	WaveBar,
	WaveContainer,
} from './ChatInputBar.styles';

const SendIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path d="M22 2 11 13" />
		<path d="M22 2 15 22l-4-9-9-4 20-7Z" />
	</svg>
);

const PhotoIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
		<rect x="3" y="3" width="18" height="18" rx="2" />
		<circle cx="8.5" cy="8.5" r="1.5" />
		<polyline points="21 15 16 10 5 21" />
	</svg>
);

const VideoIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
		<polygon points="23 7 16 12 23 17 23 7" />
		<rect x="1" y="5" width="15" height="14" rx="2" />
	</svg>
);

const ChatInputBar = forwardRef(({
	message,
	setMessage,
	handleKeyPress,
	isSendMode,
	handleActionClick,
	onRequestPhoto,
	onRequestVideo,
	isRecording,
	handleCancelRecording,
	isTranscribing,
	isComposerLocked = false,
	isVoiceCallActive,
	voiceCallPhase,
	voiceWaveMode = 'silent',
}, ref) => {
	const t = useLanguageData('chat');

	const voiceCallStatus = (() => {
		if (voiceWaveMode === 'character') return t.voiceCallSpeaking;
		if (voiceWaveMode === 'user') return t.voiceCallYouSpeaking;
		if (voiceCallPhase === 'listening') return t.voiceCallStartSpeaking;
		if (voiceCallPhase === 'processing') return t.voiceCallProcessing;
		return t.voiceCallActive;
	})();

	const isVoiceWaveActive = voiceWaveMode !== 'silent';
	const isInputLocked = isTranscribing || isComposerLocked;

	const handleInputChange = (event) => {
		if (isInputLocked) {
			return;
		}

		setMessage(event.target.value.slice(0, MAX_CHAT_MESSAGE_LENGTH));
	};

	const handleInputKeyDown = (event) => {
		if (isInputLocked) {
			return;
		}

		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			if (message.trim()) {
				handleKeyPress(event);
			}
		}
	};

	const handleInputBlur = () => {
		resetMobileViewportAfterKeyboard();
	};

	return (
		<Composer $voiceCallActive={isVoiceCallActive}>
			{isVoiceCallActive ? (
				<VoiceCallComposer $waveMode={voiceWaveMode} aria-live="polite">
					<VoiceCallStatus $waveMode={voiceWaveMode}>{voiceCallStatus}</VoiceCallStatus>
					<VoiceCallWave aria-hidden="true">
						{VOICE_CALL_WAVE_CONFIG.map((bar, index) => (
							<VoiceCallWaveBar
								key={index}
								duration={bar.duration}
								delay={bar.delay}
								$active={isVoiceWaveActive}
								$waveMode={voiceWaveMode}
							/>
						))}
					</VoiceCallWave>
				</VoiceCallComposer>
			) : isRecording || isTranscribing ? (
				<RecordingWrapper>
					<TrashButton
						onClick={handleCancelRecording}
						title={t.cancelRecording}
						disabled={isTranscribing}
					>
						<img src={TrashIcon} alt="" />
					</TrashButton>
					<WaveContainer>
						{WAVE_BARS_CONFIG.map((bar, index) => (
							<WaveBar
								key={index}
								duration={bar.duration}
								delay={bar.delay}
								isEdge={bar.isEdge}
								style={{
									animationPlayState: isTranscribing ? 'paused' : 'running',
									opacity: isTranscribing ? 0.4 : 1,
								}}
							/>
						))}
					</WaveContainer>
				</RecordingWrapper>
			) : (
				<>
					<ComposerActionButton
						type="button"
						onClick={onRequestPhoto}
						disabled={isInputLocked}
						$iconOnly
						aria-label={t.sendMePhoto}
					>
						<PhotoIcon />
					</ComposerActionButton>
					<ComposerActionButton
						type="button"
						onClick={onRequestVideo}
						disabled={isInputLocked}
						$iconOnly
						aria-label={t.sendMeVideo}
					>
						<VideoIcon />
					</ComposerActionButton>
					<ComposerInput
						ref={ref}
						type="text"
						placeholder={isComposerLocked ? t.processingMessage : t.writeMessage}
						value={message}
						maxLength={MAX_CHAT_MESSAGE_LENGTH}
						onChange={handleInputChange}
						onKeyDown={handleInputKeyDown}
						onBlur={handleInputBlur}
						disabled={isInputLocked}
						autoComplete="off"
						inputMode="text"
						enterKeyHint="send"
					/>
				</>
			)}
			<SendActionButton
				type="button"
				onClick={handleActionClick}
				disabled={
					isVoiceCallActive
					|| isInputLocked
					|| ((isSendMode && !message.trim() && !isRecording) || isTranscribing)
				}
				aria-label={t.sendMessage}
				$hidden={isVoiceCallActive}
			>
				<SendIcon />
			</SendActionButton>
		</Composer>
	);
});

ChatInputBar.displayName = 'ChatInputBar';

export default ChatInputBar;
